/**
 * Recover from stale-chunk failures after a redeploy.
 *
 * Netlify deploys are atomic: every deploy replaces the hashed asset files, so a
 * tab opened before a deploy holds an index.html whose lazy-route chunks no
 * longer exist. The first navigation to a not-yet-loaded route then 404s, the
 * SPA fallback rewrites the chunk URL to index.html, and the browser reports
 * "Expected a JavaScript-or-Wasm module script but the server responded with a
 * MIME type of 'text/html'" — the route never renders. Vite surfaces exactly
 * this as a `vite:preloadError` event; reloading fetches the new index.html and
 * its live chunks, which is the whole fix.
 */

// One reload attempt per window; inside it, let the error surface instead of
// looping (a reload that did not fix the chunk means something else is wrong).
export const RELOAD_DEBOUNCE_MS = 30_000;
const RELOAD_AT_KEY = 'hopdrive:stale-chunk-reload-at';

export function handleStaleChunk(
  event: { preventDefault: () => void },
  storage: Pick<Storage, 'getItem' | 'setItem'> = sessionStorage,
  reload: () => void = () => window.location.reload(),
  now: () => number = Date.now
): boolean {
  const last = storage.getItem(RELOAD_AT_KEY);
  if (last !== null && now() - Number(last) < RELOAD_DEBOUNCE_MS) return false;
  storage.setItem(RELOAD_AT_KEY, String(now()));
  event.preventDefault();
  reload();
  return true;
}

export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', event => {
    handleStaleChunk(event as unknown as { preventDefault: () => void });
  });
}
